// src/pages/brand/BrandStudio.tsx
import { useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import type { PlanKey } from "../../config/plans";

type ModeShellContext = {
  searchQuery?: string;
  mePlan?: PlanKey;
  credits?: number | null;
};

function prettyPlanName(plan: PlanKey): string {
  if (plan === "free") return "Free";
  if (plan === "basic") return "Basic";
  if (plan === "pro") return "Pro";
  return "Premium";
}

function isPlanKey(v: unknown): v is PlanKey {
  return v === "free" || v === "basic" || v === "pro" || v === "premium";
}

export default function BrandStudio() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ✅ viene del AppShell (topbar): búsqueda + plan + créditos reales
  const outletCtx = useOutletContext<ModeShellContext>();
  const shellSearch = outletCtx?.searchQuery ?? "";
  const credits = typeof outletCtx?.credits === "number" ? outletCtx.credits : outletCtx?.credits ?? null;

  // ✅ plan real si viene del shell; si no, fallback a user.plan
  const mePlan: PlanKey = useMemo(() => {
    const fromShell = outletCtx?.mePlan;
    if (fromShell && isPlanKey(fromShell)) return fromShell;

    const p = (user as any)?.plan;
    return isPlanKey(p) ? p : "free";
  }, [outletCtx?.mePlan, user]);

  // ✅ actividad (placeholder hasta conectar historial real de Brand)
  const totalPieces: number = 0;
  const totalProjects: number = 0;

  return (
    <section className="max-w-5xl">
      {/* ✅ HEADER 1:1 como Creator */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Brand Mode</div>
          <h1 className="text-2xl md:text-3xl font-bold mt-2">Tu panel de marca</h1>
          <p className="text-gray-300/90 mt-2 text-sm md:text-base max-w-2xl leading-relaxed">
            Aquí organizas tus campañas y generas piezas con consistencia. Empieza por crear un proyecto o genera tu primera pieza.
          </p>

          <div className="mt-3 text-[11px] text-gray-500">
            ✅ Tip para “sentir” la diferencia: crea rápido el concepto y exporta en 2K/4K/8K al final según tu plan.
          </div>

          {!!shellSearch.trim() && (
            <div className="mt-3 inline-flex items-center gap-2 text-[11px] px-3 py-1.5 rounded-full border border-white/10 bg-black/30 text-gray-200">
              🔎 Filtro activo: <span className="text-gray-100 font-semibold">“{shellSearch.trim()}”</span>
              <span className="text-gray-500">(limpia el buscador arriba para ver todo)</span>
            </div>
          )}
        </div>

        {/* ✅ SOLO 2 BOTONES (sin duplicar el sidebar) */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate("/brand/create")}
            className="px-4 py-2 rounded-xl text-sm font-semibold
              bg-gradient-to-r from-indigo-500 to-purple-500
              hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30"
          >
            ✨ Crear pieza
          </button>

          <button
            onClick={() => navigate("/brand/projects")}
            className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
          >
            📁 Proyectos
          </button>
        </div>
      </div>

      {/* ✅ STATS 1:1 */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs text-gray-400">Plan</div>
          <div className="mt-1 text-lg font-semibold">{prettyPlanName(mePlan)}</div>
          <div className="mt-2 text-[11px] text-gray-500">
            Salida: <span className="text-gray-300">2K / 4K / 8K (según plan)</span>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs text-gray-400">Créditos</div>
          <div className="mt-1 text-lg font-semibold">{credits === null ? "…" : credits}</div>
          <div className="mt-2 text-[11px] text-gray-500">Tip: exporta en alta calidad solo al final para optimizar créditos.</div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs text-gray-400">Actividad</div>
          <div className="mt-1 text-lg font-semibold">{totalPieces} piezas</div>
          <div className="mt-2 text-[11px] text-gray-500">
            {totalProjects} proyecto{totalProjects === 1 ? "" : "s"} · Organiza para repetir campañas.
          </div>
        </div>
      </div>

      {/* ✅ ÚLTIMAS PIEZAS 1:1 */}
      <div className="mt-10">
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm font-semibold text-gray-100">Últimas piezas</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/brand/create")}
              className="text-[11px] px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
            >
              Ver en Generador →
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="col-span-full rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-gray-400">
            <div className="text-base font-semibold text-gray-200">Aún no tienes piezas</div>
            <p className="text-sm mt-2">Crea tu primera pieza para que aquí aparezca tu historial.</p>
            <div className="mt-5">
              <button
                onClick={() => navigate("/brand/create")}
                className="px-5 py-3 bg-white text-black rounded-xl font-semibold hover:bg-gray-200 transition"
              >
                Ir al Generador →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


